import React, { useState, useEffect, useMemo } from 'react';
import {
    TextField,
    Tooltip,
    Autocomplete,
    Popper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import { toast } from 'react-toastify';
import SaveIcon from '@mui/icons-material/SaveAsRounded';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { styled } from '@mui/system';
import AssetService from '../../../Services/assets.service';
import Constants from '../../../assests/Constants/constants';
import dayjs from 'dayjs';
import { calculateVatAmount } from '../../../Utils/GetVatValue';
import { useDirectionByLanguage } from '../../../hooks/useDirectionByLanguage';
import { isAssetSold } from '../../../Utils/assetSaleDisplayUtils';
import ChooseAsset from '../../Widgets/ChooseAsset/ChooseAsset';
import AssetModalShell from './AssetModalShell/AssetModalShell';
import shell from './AssetModalShell/AssetModalShell.module.scss';
import { xtramInputProps, xtramTextAlign, xtramDateInputSx, xtramPopperSx } from './AssetModalShell/assetModalInputs';
import AssetVehicleSection from './AssetVehicleSection.jsx';
import { ASSET_TYPE } from '../../../Services/expenseAdjustments.service';


const StyledListItem = styled('li')({
    cursor: 'pointer',
});



const StyledPopper = styled(Popper)({
    zIndex: 1305,
    ...xtramPopperSx,
});

const AddAsset = ({
    open,
    onClose,
    onSave,
    handleSave,
    assets,
    viewAttachDocument,
    assignedDocument,
    assetGroups,
    assetRelations = [],
    caseId,
    token,
    client,
    user,
    fetchEnums,
    refreshAssets,
    onOpenAddGroupModal,
    taxYear,
}) => {

    const { i18n, t } = useTranslation();
    const direction = useDirectionByLanguage(i18n);


    const [newAsset, setNewAsset] = useState({
        name: '',
        originalPrice: '',
        depreciationPercentage: '',
        purchaseDate: dayjs().format('YYYY-MM-DD'),
        activationDate: dayjs().format('YYYY-MM-DD'), // defaults to purchaseDate
        assetNumber: '',
        assetGroupId: '',
        assetType: ASSET_TYPE.GENERAL,
        vehicle: null,
    });

    // Single-select: the existing asset that this new asset is an addition to.
    // The picked asset becomes the ROOT of an AssetRelation, the new asset becomes its CHILD.
    const [selectedParentAsset, setSelectedParentAsset] = useState(null);

    // Compute the next sensible SerialNumber (max of existing + 1) so the user gets a
    // pre-filled default when the modal opens. Treats non-numeric values as 0.
    const nextAssetNumber = useMemo(() => {
        if (!Array.isArray(assets) || assets.length === 0) return '1';
        const max = assets.reduce((acc, a) => {
            const n = Number(a?.assetNumber);
            return Number.isFinite(n) && n > acc ? n : acc;
        }, 0);
        return String(max + 1);
    }, [assets]);

    // When the modal opens, pre-fill SerialNumber if the user hasn't typed one yet.
    useEffect(() => {
        if (!open) return;
        setNewAsset((prev) => (prev.assetNumber ? prev : { ...prev, assetNumber: nextAssetNumber }));
    }, [open, nextAssetNumber]);

    // Only show assets that can act as a ROOT — i.e. assets that are NOT already
    // a child of any existing AssetRelation. Such assets would be rejected by the
    // server (validation #6: a root cannot currently be a child).
    const baseEligibleParentAssets = useMemo(() => {
        if (!Array.isArray(assets)) return [];

        const childIdsLinkedElsewhere = new Set();
        (assetRelations || []).forEach((rel) => {
            const ids = Array.isArray(rel?.relatedAssetIds) && rel.relatedAssetIds.length > 0
                ? rel.relatedAssetIds
                : (rel?.relatedAssets || []).map((ra) => ra?.id).filter(Boolean);
            ids.forEach((id) => childIdsLinkedElsewhere.add(id));
        });

        return assets
            .filter((a) => a && a.id && !childIdsLinkedElsewhere.has(a.id) && !isAssetSold(a))
            .slice()
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [assets, assetRelations]);

    const selectedAssetGroupId = newAsset.assetGroupId || '';

    const eligibleParentAssets = useMemo(() => {
        const list = selectedAssetGroupId
            ? baseEligibleParentAssets.filter((a) => a.assetGroupId === selectedAssetGroupId)
            : baseEligibleParentAssets;
        return list;
    }, [baseEligibleParentAssets, selectedAssetGroupId]);

    useEffect(() => {
        if (!selectedAssetGroupId || !selectedParentAsset) return;
        if (selectedParentAsset.assetGroupId !== selectedAssetGroupId) {
            setSelectedParentAsset(null);
        }
    }, [selectedAssetGroupId, selectedParentAsset]);

    useEffect(() => {
        if (!selectedParentAsset) return;
        if (isAssetSold(selectedParentAsset)) {
            setSelectedParentAsset(null);
        }
    }, [selectedParentAsset, assets]);

    const fieldErrors = {
        // ——— required single-value fields ———
        name: !newAsset.name?.trim(),
        originalPrice:
            newAsset.originalPrice?.toString().trim() === '' ||
            Number(newAsset.originalPrice) <= 0,
        depreciationPercentage:
            newAsset.depreciationPercentage?.toString().trim() === '' ||
            Number(newAsset.depreciationPercentage) < 0,
        purchaseDate: !newAsset.purchaseDate,
    };

    const inputPropsWithError = (hasError) => xtramInputProps(direction, hasError);

    const [isSaveDisabled, setIsSaveDisabled] = useState(true); // State to manage save button disabled state
    const [showChooseExistingAsset, setShowChooseExistingAsset] = useState(false);

    useEffect(() => {
        if (!open) {
            setShowChooseExistingAsset(false);
        }
    }, [open]);


    // make me a handleClickedSave which calls handleSave then setAsset as empty
    const handleClickedSave = async (asset) => {
        handleSave({
            ...asset,
            parentAssetId: selectedParentAsset?.id || null,
        });
        setNewAsset({
            name: '',
            originalPrice: '',
            depreciationPercentage: '',
            purchaseDate: dayjs().format('YYYY-MM-DD'),
            activationDate: dayjs().format('YYYY-MM-DD'), // defaults to purchaseDate
            assetNumber: '',
            assetGroupId: '',
            assetType: ASSET_TYPE.GENERAL,
            vehicle: null,
        });
        setSelectedParentAsset(null);
    }

    const handleClose = () => {
        setNewAsset({
            name: '',
            originalPrice: '',
            depreciationPercentage: '',
            purchaseDate: dayjs().format('YYYY-MM-DD'),
            activationDate: dayjs().format('YYYY-MM-DD'), // defaults to purchaseDate
            assetNumber: '',
            assetGroupId: '',
            assetType: ASSET_TYPE.GENERAL,
            vehicle: null,
        });
        setSelectedParentAsset(null);
        onClose();
    }


    useEffect(() => {


        setNewAsset(prev => ({ ...prev, documentItSelf: assignedDocument }));
        if (assignedDocument) {
            if (assignedDocument?.type === Constants.EntryType.Expense) {
                setNewAsset(prev => ({ ...prev, expenseId: assignedDocument.id }));
                setNewAsset(prev => ({ ...prev, journalEntryId: null }));
            } else if (assignedDocument?.type === Constants.EntryType.JournalEntry) {
                setNewAsset(prev => ({ ...prev, journalEntryId: assignedDocument.id }));
                setNewAsset(prev => ({ ...prev, expenseId: null }));
            } else {
                console.error('Unknown document type:');
                return; // Prevent further execution if the type is unknown
            }
            setNewAsset(prev => ({
                ...prev,
                originalPrice: Math.floor(assignedDocument.amount - calculateVatAmount(assignedDocument.amount, assignedDocument.vat, assignedDocument.documentDate)),
                purchaseDate: assignedDocument.documentDate,
                activationDate: assignedDocument.documentDate,
                name: assignedDocument.description,
            }));

        }

    }, [assignedDocument]);

    // Calculate the default assetNumber when the component mounts or when assets change
    useEffect(() => {
        if (assets && assets.length > 0) {
            // Find the maximum assetNumber in the assets array
            const maxAssetNumber = Math.max(...assets.map(asset => asset.assetNumber || 0));
            // Set the default assetNumber to maxAssetNumber + 1
            setNewAsset(prev => ({ ...prev, assetNumber: maxAssetNumber + 1 }));
        } else {
            // If there are no assets, start with 1
            setNewAsset(prev => ({ ...prev, assetNumber: 1 }));
        }
    }, [assets]);

    // Validation function to check if required fields are filled
    const validateFields = () => {
        const { name, originalPrice, purchaseDate, depreciationPercentage } = newAsset;
        return (
            name?.trim() !== '' &&
            originalPrice?.toString().trim() !== '' &&
            purchaseDate !== null &&
            depreciationPercentage?.toString().trim() !== ''
        );
    };


    // Update validation state whenever newAsset changes
    useEffect(() => {
        setIsSaveDisabled(!validateFields());
    }, [newAsset]);

    const inputPropsStyle = xtramInputProps(direction);
    const inputStyle = xtramTextAlign(direction);

    const handleChange = (field, value) => {
        if (field === 'assetGroup') {
            if (!value) {
                setNewAsset(prev => ({ ...prev, assetGroupId: '', depreciationPercentage: '' }));
                return;
            }
            const assetGId = value?.assetGroupId;
            setNewAsset(prev => ({ ...prev, ['depreciationPercentage']: value.depreciationPercentage }));
            setNewAsset(prev => ({ ...prev, ['assetGroupId']: assetGId }));
        } else if (field === 'purchaseDate' || field === 'activationDate') {
            setNewAsset(prev => ({ ...prev, [field]: value }));
        } else {
            setNewAsset(prev => ({ ...prev, [field]: value }));
        }
    };

    if (!open) return null;

    const popper = (props) => (
        <StyledPopper {...props} style={{ ...props.style, direction }} />
    );

    const label = (text, required = false) => (
        <span className={shell.fieldLabel}>
            <span>{required && <span className={shell.requiredMark}>*</span>}{text}</span>
        </span>
    );

    const attachedDocument = newAsset.documentItSelf;

    return (
        <>
            <AssetModalShell
                open={open}
                onClose={handleClose}
                title={t('AddAsset')}
                direction={direction}
                size="md"
                footer={(
                    <>
                        <button type="button" className={shell.secondaryBtn} onClick={handleClose}>
                            {t('Cancel')}
                        </button>
                        <button
                            type="button"
                            className={shell.primaryBtn}
                            onClick={() => handleClickedSave(newAsset)}
                            disabled={isSaveDisabled}
                        >
                            <SaveIcon />
                            {t('Save')}
                        </button>
                    </>
                )}
            >
                <section className={shell.section}>
                    <h3 className={shell.sectionTitle}>{t('AssetInformation') || 'פרטי נכס'}</h3>
                    <div className={shell.fieldGrid}>
                        <div className={shell.field}>
                            {label(t('AssetName'), true)}
                            <TextField
                                fullWidth
                                placeholder={t('AssetName')}
                                value={newAsset.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                variant="standard"
                                InputProps={inputPropsWithError(fieldErrors.name)}
                                inputProps={inputStyle}
                            />
                        </div>
                        <div className={shell.field}>
                            {label(t('SerialNumber'))}
                            <TextField
                                fullWidth
                                placeholder={t('SerialNumber')}
                                value={newAsset.assetNumber}
                                onChange={(e) => handleChange('assetNumber', e.target.value)}
                                variant="standard"
                                InputProps={inputPropsStyle}
                                inputProps={inputStyle}
                            />
                        </div>
                        <div className={shell.field}>
                            {label(t('AssetGroup'))}
                            <Autocomplete
                                options={assetGroups}
                                getOptionLabel={(option) => `${option.assetGroupId} - ${option.name}`}
                                value={assetGroups.find((option) => option.assetGroupId === newAsset.assetGroupId) || null}
                                onChange={(event, newValue) => {
                                    if (!newValue) {
                                        setNewAsset(prev => ({ ...prev, assetGroupId: '', depreciationPercentage: '' }));
                                    } else {
                                        handleChange('assetGroup', newValue);
                                    }
                                }}
                                noOptionsText={<strong>{t('NoOptions')}</strong>}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={t('Select Asset Group')}
                                        variant="standard"
                                        InputProps={{ ...params.InputProps, ...inputPropsStyle }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Tooltip title={t('RightClickToEdit')} placement="top">
                                        <StyledListItem {...props}>
                                            {`${option.assetGroupId} - ${option.name}`}
                                        </StyledListItem>
                                    </Tooltip>
                                )}
                                PopperComponent={popper}
                            />
                        </div>
                        <div className={shell.field}>
                            {label(t('DepreciationPercentage'), true)}
                            <TextField
                                fullWidth
                                placeholder={t('DepreciationPercentage')}
                                type="number"
                                value={newAsset.depreciationPercentage}
                                onChange={(e) => handleChange('depreciationPercentage', e.target.value)}
                                variant="standard"
                                InputProps={inputPropsWithError(fieldErrors.depreciationPercentage)}
                                inputProps={inputStyle}
                            />
                        </div>
                        <div className={shell.field}>
                            {label(t('OriginalPrice'), true)}
                            <TextField
                                fullWidth
                                placeholder={t('OriginalPrice')}
                                type="number"
                                value={newAsset.originalPrice}
                                onChange={(e) => handleChange('originalPrice', e.target.value)}
                                variant="standard"
                                InputProps={inputPropsWithError(fieldErrors.originalPrice)}
                                inputProps={inputStyle}
                            />
                        </div>
                        <div className={shell.field}>
                            {label(t('ChooseAssetIfAdditionToAsset'))}
                            <Autocomplete
                                options={eligibleParentAssets}
                                getOptionLabel={(option) => `${option.name} - ${option.assetNumber}`}
                                value={selectedParentAsset}
                                onChange={(event, newValue) => setSelectedParentAsset(newValue || null)}
                                isOptionEqualToValue={(option, val) => option?.id === val?.id}
                                noOptionsText={<strong>{t('NoOptions')}</strong>}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={t('SelectAnAsset')}
                                        variant="standard"
                                        InputProps={{ ...params.InputProps, ...inputPropsStyle }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <StyledListItem {...props}>
                                        {`${option.name} - ${option.assetNumber}`}
                                    </StyledListItem>
                                )}
                                PopperComponent={popper}
                            />
                        </div>
                    </div>
                </section>

                <AssetVehicleSection
                    assetType={newAsset.assetType}
                    vehicle={newAsset.vehicle}
                    onChange={(assetType, vehicle) => setNewAsset((prev) => ({ ...prev, assetType, vehicle }))}
                    token={token}
                    direction={direction}
                    taxYear={taxYear}
                />

                <section className={shell.section}>
                    <h3 className={shell.sectionTitle}>{t('Dates') || 'תאריכים'}</h3>
                    <div className={shell.fieldGrid}>
                        <div className={shell.field}>
                            {label(t('PurchaseDate'), true)}
                            <CustomDatePicker
                                date={newAsset.purchaseDate}
                                onDateChange={(date) => handleChange('purchaseDate', date ? date.toISOString() : null)}
                                height="var(--xtram-control-height, 40px)"
                                inputSx={xtramDateInputSx(fieldErrors.purchaseDate)}
                                clearable
                            />
                        </div>
                        <div className={shell.field}>
                            {label(t('ActivationDate'), true)}
                            <CustomDatePicker
                                date={newAsset.activationDate}
                                onDateChange={(date) => handleChange('activationDate', date ? date.toISOString() : null)}
                                height="var(--xtram-control-height, 40px)"
                                inputSx={xtramDateInputSx()}
                                clearable
                            />
                        </div>
                    </div>
                </section>

                <section className={shell.section}>
                    <h3 className={shell.sectionTitle}>{t('AttachDocument')}</h3>
                    <div className={shell.toolbar}>
                        {attachedDocument && (
                            <span className={shell.chip}>
                                <DescriptionIcon sx={{ fontSize: 16 }} />
                                {`${attachedDocument.type === 1 ? t('Expense') : t('JournalEntry')} - ${attachedDocument.amount}₪`}
                            </span>
                        )}
                        <button type="button" className={`${shell.blueBtn} ${shell.small}`} onClick={() => viewAttachDocument()}>
                            <AttachFileIcon />
                            {attachedDocument ? t('EditAttachedDocument') : t('AttachDocument')}
                        </button>
                        {attachedDocument?.type === Constants.EntryType.Expense && (
                            <button type="button" className={`${shell.secondaryBtn} ${shell.small}`} onClick={() => setShowChooseExistingAsset(true)}>
                                {t('ChooseFromExistingAssets')}
                            </button>
                        )}
                    </div>
                </section>
            </AssetModalShell>
            {showChooseExistingAsset && assignedDocument && caseId && token && client && fetchEnums && (
                <ChooseAsset
                    open={showChooseExistingAsset}
                    onClose={() => setShowChooseExistingAsset(false)}
                    type="Expenses"
                    itemId={assignedDocument.id}
                    fullItem={assignedDocument}
                    client={client}
                    token={token}
                    saleEquipment={false}
                    fetchEnums={fetchEnums}
                    user={user}
                    assetGroups={assetGroups || []}
                    editedItemVat={assignedDocument.vat}
                    setShowAddGroupModal={onOpenAddGroupModal}
                    onDocumentLinked={async () => {
                        if (typeof refreshAssets === 'function') {
                            await refreshAssets();
                        }
                        handleClose();
                    }}
                />
            )}
        </>
    );
};

export default AddAsset;